import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { withMainLayout } from 'hocs';
import Preloader from 'components/Preloader';
import { isEmpty } from 'lodash';

import { generateDFSClientId } from 'utils';
import { useNavigate } from 'react-router-dom';

import { IsAllDataNull } from 'validation/validation';
import { useOptionList } from 'hooks/useOptionList';

import 'ui-library/components/Button/Button.css';
import 'ui-library/components/Label/Label.css';
import 'ui-library/components/TextInput/TextInput.css';
import 'ui-library/components/DatePicker/DatePicker.css';
import '../../../../../../../../widgets/PersonalDetails/PersonalDetails.css';

import './PersonalDetails.css';
import { adaptCountry } from 'widgets/PersonalDetails/utils/adapters';
import { Icon, Title } from 'ui-library';
import { NewAddressModal } from 'pages/ClientOverview/pages/ClientConnections/components/NewConnections';
import { ICON_EDIT_OUTLINE } from 'ui-library/components/Icon';
import { useUpdateConnectionMailingAddress } from 'components/Connections/hooks/useUpdateConnectionMailingAddress';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { OPTION_LIST } from '../../../../../../../../constants/constants';
import { errorModel } from './models/errorModel';
import { EditablePersonalData } from '../../../../components/EditablePersonalData';

const PersonalDetails = ({ datasource }: { datasource: () => any }) => {
    const navigate = useNavigate();
    const { t, i18n: { language } } = useTranslation();

    const [isLoadingPersonalData, setLoadingPersonalData] = useState(false);
    const [occupancyList, setOccupancyList] = useState<Record<string, any[]>>({});
    const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
    const [mainOwnerErrors, setMainOwnerErrors] = useState<any>(null);
    const [jointOwnerErrors, setJointOwnerErrors] = useState<any>(null);

    const { updateContactGroupAddress } = useUpdateConnectionMailingAddress();

    const {
        mainOwnerDetails,
        isJointAccount,
        portfolioOwners,
        contactGroupAddress,
        contactGroupId,
        jointAccountDetails,
    } = datasource();

    const { lists: [optionGender, optionCountry] } = useOptionList([OPTION_LIST.gender, OPTION_LIST.country]);
    const jointAccountClientId = useMemo(() => portfolioOwners?.find((f: any) => f.id !== mainOwnerDetails?.clientId)?.id, [mainOwnerDetails?.clientId, portfolioOwners]);

    const editPersonalData = (clientId: number) => {
        navigate(`/client/${generateDFSClientId(clientId)}/details`);
    };

    const mailingAddress = useMemo(() => {
        let result = '';

        if (contactGroupAddress?.street) result += `${contactGroupAddress?.street}, `;
        if (contactGroupAddress?.zipCity || contactGroupAddress?.city) {
            result += `${contactGroupAddress?.zipCity} ${contactGroupAddress?.city}, `;
        }
        if (contactGroupAddress?.country && !isEmpty(optionCountry)) {
            result += adaptCountry(optionCountry, contactGroupAddress?.country).label;
        }

        return result || '-';
    }, [contactGroupAddress, optionCountry]);

    const {
        data: occupationDataForMainOwner,
        error: mainOwnerOccupationError,
        isLoading: isMainOwnerOccupationLoading,
    } = useGetCustomService({
        service: 'customService',
        api: 'getOccupationsLookup',
        payload: { language, gender: mainOwnerDetails?.gender, selectedId: mainOwnerDetails?.occupation },
        enabled: !!mainOwnerDetails?.gender && !!mainOwnerDetails?.occupation && !occupancyList[mainOwnerDetails?.gender],
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 2,
    });

    const {
        data: occupationDataForJointOwner,
        error: jointOwnerOccupationError,
        isLoading: isJointOwnerOccupationLoading,
    } = useGetCustomService({
        service: 'customService',
        api: 'getOccupationsLookup',
        payload: { language, gender: jointAccountDetails?.gender, selectedId: jointAccountDetails?.occupation },
        enabled: !!jointAccountDetails?.gender && !!jointAccountDetails?.occupation && !occupancyList[jointAccountDetails?.gender],
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 2,
    });

    useEffect(() => {
        if (occupationDataForMainOwner?.data) {
            setOccupancyList(prev => ({
                ...prev,
                [mainOwnerDetails?.gender]: occupationDataForMainOwner?.data?.map(({ id, name, disabled }: any) => ({ id, label: name, disabled })),
            }));
        }
        if (occupationDataForJointOwner?.data) {
            setOccupancyList(prev => ({
                ...prev,
                [jointAccountDetails?.gender]: occupationDataForJointOwner?.data?.map(({ id, name, disabled }: any) => ({ id, label: name, disabled })),
            }));
        }
    }, [
        occupationDataForMainOwner?.data,
        mainOwnerDetails?.gender,
        occupationDataForJointOwner?.data,
        jointAccountDetails?.gender,
    ]);

    const onContinue = useCallback(async () => {
        const errors = errorModel(mainOwnerDetails);
        const jaErrors = errorModel(jointAccountDetails);

        if (!IsAllDataNull(errors) && !IsAllDataNull(jaErrors)) {
            setMainOwnerErrors(errors);
            setJointOwnerErrors(jaErrors);

            return;
        }

        setStepComplete(true);
    }, [mainOwnerDetails, jointAccountDetails]);

    useEffect(() => {
        setLoadingPersonalData(true);
        if (optionCountry !== undefined && optionGender !== undefined && mainOwnerDetails?.clientId && (isJointAccount ? jointAccountDetails?.clientId : true)) {
            setLoadingPersonalData(false);
        }
    }, [optionGender, optionCountry, isJointAccount, mainOwnerDetails?.clientId, jointAccountDetails?.clientId]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: onContinue,
        isContinueDisabled: isLoadingPersonalData,
        isBackDisabled: true,
        isContinueLoading: isLoadingPersonalData,
    });

    return (
        <div className={`client-details ${isJointAccount ? 'joint-details' : ''}`}>
            <Preloader
                isLoading={isLoadingPersonalData || isMainOwnerOccupationLoading || isJointOwnerOccupationLoading}
                error={mainOwnerOccupationError || jointOwnerOccupationError}
            >
                <div className="details-row">
                    <EditablePersonalData
                        data={mainOwnerDetails}
                        editPersonalData={() => editPersonalData(mainOwnerDetails?.clientId)}
                        t={t}
                        errorData={mainOwnerErrors}
                        optionCountry={optionCountry}
                        optionGender={optionGender}
                        optionOccupancy={occupancyList[mainOwnerDetails?.gender]}
                        isChangePlan
                    />
                </div>
                {isJointAccount && (
                    <div className="details-row">
                        <EditablePersonalData
                            data={jointAccountDetails}
                            editPersonalData={() => editPersonalData(jointAccountClientId)}
                            t={t}
                            errorData={jointOwnerErrors}
                            optionCountry={optionCountry}
                            optionGender={optionGender}
                            optionOccupancy={occupancyList[jointAccountDetails?.gender]}
                            isChangePlan
                        />
                    </div>
                )}

                {isJointAccount && (
                    <NewAddressModal
                        onSubmit={async (data: any) => {
                            setEditAddressModalOpen(false);
                            if (contactGroupId) {
                                await updateContactGroupAddress(contactGroupId, {
                                    language: data.mailRecipientLanguage,
                                    addLine1: data.mailRecipientAddressLine1,
                                    addLine2: data.mailRecipientAddressLine2,
                                    street: data.mailRecipientAddressLine3,
                                    zipCity: data.mailRecipientAddressLine4,
                                    city: data.mailRecipientAddressLine5,
                                    country: data.mailRecipientAddressCountry,
                                });
                            }
                        }}
                        outsideClickHappened={editAddressModalOpen}
                        setEditAddressModalOpen={setEditAddressModalOpen}
                        initData={{
                            mailRecipientLanguage: contactGroupAddress?.language,
                            mailRecipientAddressLine1: contactGroupAddress?.addLine1,
                            mailRecipientAddressLine2: contactGroupAddress?.addLine2,
                            mailRecipientAddressLine3: contactGroupAddress?.street,
                            mailRecipientAddressLine4: contactGroupAddress?.zipCity,
                            mailRecipientAddressLine5: contactGroupAddress?.city,
                            mailRecipientAddressCountry: contactGroupAddress?.country,
                        }}
                        customRenderComponent={(
                            <div className="DataList__Item--hspan">
                                <div className="LinkedHeader">
                                    <Title type={2} data-cy="full_name">
                                        {t('dynamicCRM.mailRecipientFieldset')}
                                    </Title>
                                    <button
                                        className="button-link"
                                        type="button"
                                        data-cy="edit_personal_details"
                                        onClick={() => setEditAddressModalOpen(true)}
                                    >
                                        <Icon size={24} type={ICON_EDIT_OUTLINE} />
                                        {t('interaction.edit')}
                                    </button>
                                </div>
                                <dd data-cy="city_zip">{mailingAddress}</dd>
                            </div>
                        )}
                    />
                )}
            </Preloader>
        </div>
    );
};

export default withMainLayout(PersonalDetails);
